import {Component, OnInit, OnDestroy, ViewChild, AfterViewInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {UntypedFormBuilder, UntypedFormGroup, Validators, ReactiveFormsModule, FormsModule} from '@angular/forms';
import {Router, ActivatedRoute} from '@angular/router';
import {faPen, faRedo, faThumbtack, faTrash, faCopy, faCheck, faDownload} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop';

import {UserService} from '../../services/user/user.service';
import {HouseholdService} from '../../services/property/household.service';
import {FoodPreferencesService} from '../../services/food-preferences/food-preferences.service';
import {ConsumptionCheckService} from '../../services/consumption/consumption-check.service';
import {EMPTY_FOOD_PREFERENCES, FoodPreferences} from '../../services/food-preferences/food-preferences.models';
import {StoreService} from '../../services/property/store.service';
import {InvitationService} from '../../services/invitation/invitation.service';
import {UiToastService} from '../../shared/ui';
import {ConfirmService} from '../../services/confirm/confirm.service';
import {AppEventsService} from '../../services/app-events/app-events.service';
import {PageHeaderService} from '../../services/page-header/page-header.service';

import {HouseholdWithStores, Household, HouseholdProviderSetting} from '../../models/household';
import {Store} from '../../models/store';
import {TEMPERATURE_ZONES} from '../../models/temperature-zone';
import {InvitationResponse} from '../../models/invitationResponse';
import {UserWithDefaults} from '../../models/user';
import {DEFAULT_REMINDER_SETTINGS, ReminderSettings} from '../../models/consumption-check';
import {Member} from '../../models/member';
import { AppLogger, AppLoggerService } from '../../core/logging';

import {
  UiButtonComponent,
  UiListComponent,
  UiListItemComponent,
  UiBadgeComponent,
  UiCardComponent,
  UiModalComponent,
  UiRowActionsComponent,
  RowAction
} from '../../shared/ui';

// add missing rxjs imports
import {Subject} from 'rxjs';
import {distinctUntilChanged, map, takeUntil} from 'rxjs/operators';

type SettingsSection = 'general' | 'members' | 'stores' | 'tasks' | 'export' | 'providers' | 'preferences' | 'reminders' | 'danger';

interface MenuItem {
  id: SettingsSection;
  label: string;
  icon?: any;
}

@Component({
  selector: 'app-manage-default-household',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    FontAwesomeModule,
    DragDropModule,
    UiButtonComponent,
    UiListComponent,
    UiListItemComponent,
    UiBadgeComponent,
    UiCardComponent,
    UiModalComponent,
    UiRowActionsComponent,
  ],
  templateUrl: './manage-default-household.component.html',
  styleUrl: './manage-default-household.component.scss',
  standalone: true
})
export class ManageDefaultHouseholdComponent implements OnInit, OnDestroy, AfterViewInit {

  private readonly log: AppLogger;

  household: HouseholdWithStores | null = null;
  householdId: number | null = null;
  userWithDefaults: UserWithDefaults | null = null;
  accessDenied = false;
  loading = true;

  // Active section
  activeSection: SettingsSection = 'general';

  // Menu items
  menuItems: MenuItem[] = [
    {id: 'general', label: 'General'},
    {id: 'members', label: 'Members & Invitations'},
    {id: 'stores', label: 'Stores'},
    {id: 'tasks', label: 'Household Tasks'},
    {id: 'export', label: 'Export Stock'},
    {id: 'providers', label: 'Catalog Providers'},
    {id: 'preferences', label: 'Food Preferences'},
    {id: 'reminders', label: 'Reminders'},
    {id: 'danger', label: 'Danger Zone'}
  ];

  // Consumption reminders. A household that never changed these has no stored
  // row, so the form starts on the same defaults the server would apply.
  reminders: ReminderSettings = {...DEFAULT_REMINDER_SETTINGS};
  loadingReminders = false;
  savingReminders = false;
  /** True when the settings could not be read — the form then shows defaults. */
  remindersUnavailable = false;
  readonly cadenceChoices = [1, 2, 3, 7, 14];
  readonly itemsPerRoundChoices = [1, 2, 3];
  readonly hourChoices = Array.from({length: 24}, (_, h) => h);

  // Food preferences — edited as comma-separated text, parsed on save; the backend
  // normalizes (trim/dedupe/bound) and returns the canonical set.
  foodPrefs: FoodPreferences = EMPTY_FOOD_PREFERENCES;
  dietsText = '';
  dislikesText = '';
  cuisinesText = '';
  customPrompt = '';
  // 0 = not set; the assistant then falls back to a sensible number per dish.
  defaultServings = 0;
  loadingPrefs = false;
  savingPrefs = false;

  // Icons
  faEdit = faPen;
  faRemove = faTrash;
  faResend = faRedo;
  faPin = faThumbtack;
  faCopy = faCopy;
  faCheck = faCheck;
  faDownload = faDownload;

  // Invitations
  openInvites: InvitationResponse[] = [];
  inviteEmail = '';
  inviting = false;

  // Household tasks
  tasksExecuting = false;
  selectedTasks: string[] = [];

  // Export stock state
  stockPrompt: string | null = null;
  stockPromptLoading = false;
  stockPromptCopied = false;
  private copyResetTimer?: ReturnType<typeof setTimeout>;

  // Catalog providers state
  providers: HouseholdProviderSetting[] = [];
  loadingProviders = false;

  // UI Modals
  @ViewChild('editHouseholdUiModal', {static: false}) editHouseholdUiModal?: UiModalComponent;
  @ViewChild('storeUiModal', {static: false}) storeUiModal?: UiModalComponent;
  @ViewChild('inviteModal', {static: false}) inviteModal?: UiModalComponent;
  @ViewChild('exportPromptModal', {static: false}) exportPromptModal?: UiModalComponent;

  // Store modal state
  storeMode: 'create' | 'edit' = 'create';
  storeSaving = false;
  storeMessage?: string;
  storeForm: UntypedFormGroup;
  activeStore?: any;
  activeStoreId?: number;
  readonly temperatureZones = TEMPERATURE_ZONES;

  // Household form state
  householdSaving = false;
  householdForm: UntypedFormGroup;

  // Delete household
  deletingHousehold = false;
  clearingProducts = false;

  // query-param sync
  private readonly sectionQueryParamKey = 'section';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private householdService: HouseholdService,
    private storeService: StoreService,
    private invitationService: InvitationService,
    private fb: UntypedFormBuilder,
    private toastService: UiToastService,
    private appLogger: AppLoggerService,
    private appEvents: AppEventsService,
    private confirm: ConfirmService,
    private router: Router,
    private pageHeader: PageHeaderService,
    private route: ActivatedRoute,
    private foodPreferencesService: FoodPreferencesService,
    private consumptionChecks: ConsumptionCheckService
  ) {
    this.log = this.appLogger.createForClass(ManageDefaultHouseholdComponent);

    this.storeForm = this.fb.group({
      isDefault: [false],
      name: ['', [Validators.required, Validators.minLength(2)]],
      temperatureZone: ['']
    });

    this.householdForm = this.fb.group({
      isDefault: [false],
      name: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  ngOnInit(): void {
    this.bindSectionToQueryParam();
    this.loadUserDefaults();
  }

  ngAfterViewInit(): void {

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.pageHeader.clearAll();
    clearTimeout(this.copyResetTimer);
  }

  loadUserDefaults(): void {
    this.loading = true;
    this.userService.getUserProfile().subscribe({
      next: (data) => {
        this.userWithDefaults = data;

        if (data.defaults?.household?.id) {
          this.householdId = data.defaults.household.id;
          this.loadHousehold(this.householdId);
          this.loadPendingInvites(this.householdId);
          this.loadProviders(this.householdId);
          this.loadFoodPreferences(this.householdId);
          this.loadReminderSettings(this.householdId);
        } else {
          this.toastService.error('No default household found. Please set one in your profile.', { durationMs: 5000 });
          this.loading = false;
        }
      },
      error: (error) => {
        this.log.error('Failed to load user defaults', error);
        this.toastService.error('Failed to load user profile', { durationMs: 5000 });
        this.loading = false;
      }
    });
  }

  loadHousehold(householdId: number): void {
    this.householdService.householdById(householdId).subscribe({
      next: (data) => {
        this.household = data;
        this.loading = false;
        this.pageHeader.setPageTitle(`${this.household?.name ?? 'Household'} - Settings`);
      },
      error: (error) => {
        if (error.status === 403) {
          this.accessDenied = true;
        }
        this.loading = false;
      }
    });
  }

  private loadFoodPreferences(householdId: number): void {
    this.loadingPrefs = true;
    this.foodPreferencesService.get(householdId).subscribe({
      next: (prefs) => {
        this.applyFoodPrefs(prefs);
        this.loadingPrefs = false;
      },
      error: (e) => {
        this.log.error('Failed to load food preferences', e, {householdId});
        this.loadingPrefs = false;
      },
    });
  }

  private applyFoodPrefs(prefs: FoodPreferences): void {
    this.foodPrefs = prefs;
    this.dietsText = (prefs.diets ?? []).join(', ');
    this.dislikesText = (prefs.dislikes ?? []).join(', ');
    this.cuisinesText = (prefs.liked_cuisines ?? []).join(', ');
    this.customPrompt = prefs.custom_prompt ?? '';
    this.defaultServings = prefs.default_servings ?? 0;
  }

  private parseList(text: string): string[] {
    return text
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }

  private loadReminderSettings(householdId: number): void {
    this.loadingReminders = true;
    this.consumptionChecks.getSettings(householdId).subscribe({
      next: (settings) => {
        this.reminders = settings;
        this.remindersUnavailable = false;
        this.loadingReminders = false;
      },
      error: (e) => {
        // Reminders are not wired up on every backend yet; showing the defaults
        // read-only is more honest than an empty panel.
        this.log.warn('Failed to load reminder settings', {householdId, status: e?.status});
        this.reminders = {...DEFAULT_REMINDER_SETTINGS};
        this.remindersUnavailable = true;
        this.loadingReminders = false;
      },
    });
  }

  saveReminderSettings(): void {
    if (this.savingReminders || !this.householdId) return;
    this.savingReminders = true;

    this.consumptionChecks.updateSettings(this.householdId, this.reminders).subscribe({
      next: (stored) => {
        this.reminders = stored;
        this.remindersUnavailable = false;
        this.savingReminders = false;
        this.toastService.success('Reminder settings saved', {durationMs: 3000});
      },
      error: (e) => {
        this.log.error('Failed to save reminder settings', e, {householdId: this.householdId});
        this.toastService.error(
          e?.status === 404
            ? 'Reminders are not available on this server yet.'
            : 'Failed to save reminder settings',
          {durationMs: 6000},
        );
        this.savingReminders = false;
      },
    });
  }

  /** "08:00" for an hour-of-day value. */
  hourLabel(hour: number): string {
    return `${String(hour).padStart(2, '0')}:00`;
  }

  saveFoodPreferences(): void {
    if (this.savingPrefs || !this.householdId) return;
    this.savingPrefs = true;

    const payload: FoodPreferences = {
      diets: this.parseList(this.dietsText),
      dislikes: this.parseList(this.dislikesText),
      liked_cuisines: this.parseList(this.cuisinesText),
      custom_prompt: this.customPrompt.trim(),
      // Coerce blank/NaN input to 0 ("not set"); the backend clamps to 0..50.
      default_servings: Math.max(0, Math.floor(Number(this.defaultServings) || 0)),
    };

    this.foodPreferencesService.save(this.householdId, payload).subscribe({
      next: (stored) => {
        this.applyFoodPrefs(stored);
        this.savingPrefs = false;
        this.toastService.success('Food preferences saved', {durationMs: 3000});
      },
      error: (e) => {
        this.log.error('Failed to save food preferences', e, {householdId: this.householdId});
        this.toastService.error('Failed to save food preferences', {durationMs: 6000});
        this.savingPrefs = false;
      },
    });
  }

  loadPendingInvites(householdId: number): void {
    this.invitationService.getInvitationsOfHousehold(householdId).subscribe({
      next: (data) => {
        this.openInvites = data;
      },
      error: (e) => {
        this.log.error('Failed to load invitations', e, { householdId });
      }
    });
  }

  // + add
  private bindSectionToQueryParam(): void {
    const allowed = new Set<SettingsSection>(this.menuItems.map(m => m.id));

    this.route.queryParamMap
      .pipe(
        map((params) => {
          const raw = params.get(this.sectionQueryParamKey);
          if (!raw) return null;
          return (allowed.has(raw as SettingsSection) ? (raw as SettingsSection) : null);
        }),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((sectionFromUrl) => {
        if (!sectionFromUrl) return;
        if (sectionFromUrl === this.activeSection) return;
        this.activeSection = sectionFromUrl;
      });
  }

  // Section navigation
  selectSection(section: SettingsSection): void {
    if (this.activeSection === section) return;

    this.activeSection = section;

    // Persist the selected section in the URL so reloads (and deep links) restore it.
    // Using replaceUrl avoids filling browser history with tab clicks.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [this.sectionQueryParamKey]: section },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  isActiveSection(section: SettingsSection): boolean {
    return this.activeSection === section;
  }

  // ==================== GENERAL SECTION ====================
  openEditHousehold(): void {
    if (!this.household) return;

    this.householdForm.reset({
      isDefault: !!this.household.is_default,
      name: this.household.name ?? ''
    });
    this.householdSaving = false;
    this.editHouseholdUiModal?.open();
  }

  closeEditHouseholdModal(): void {
    this.editHouseholdUiModal?.close();
  }

  saveHouseholdFromModal(): void {
    if (this.householdSaving || this.householdForm.invalid || !this.householdId) return;
    this.householdSaving = true;

    const payload: Household = {
      name: this.householdForm.value.name,
      is_default: this.householdForm.value.isDefault
    };

    this.householdService.update(this.householdId, payload).subscribe({
      next: () => {
        this.toastService.success('Household saved', { durationMs: 3000 });
        this.loadHousehold(this.householdId!);
        this.appEvents.notifyHouseholdChanged();
        this.householdSaving = false;
        this.editHouseholdUiModal?.close();
      },
      error: (e) => {
        this.log.error('Failed to save household', e, { householdId: this.householdId });
        this.toastService.error('Failed to save household', { durationMs: 6000 });
        this.householdSaving = false;
      }
    });
  }

  // ==================== MEMBERS SECTION ====================
  openInviteModal(): void {
    this.inviteEmail = '';
    this.inviting = false;
    this.inviteModal?.open();
  }

  closeInviteModal(): void {
    this.inviteModal?.close();
  }

  sendInvitation(): void {
    if (!this.inviteEmail || !this.householdId) return;

    this.inviting = true;
    this.invitationService.createInvite(this.householdId, this.inviteEmail).subscribe({
      next: () => {
        this.toastService.success('Invitation sent', { durationMs: 3000 });
        this.loadPendingInvites(this.householdId!);
        this.inviting = false;
        this.inviteModal?.close();
      },
      error: (e) => {
        this.log.error('Failed to send invitation', e, { householdId: this.householdId, inviteEmail: this.inviteEmail });
        this.toastService.error('Failed to send invitation', { durationMs: 6000 });
        this.inviting = false;
      }
    });
  }

  resendInvitation(invitationId: number): void {
    this.invitationService.resendInvitation(invitationId).subscribe({
      next: () => {
        this.loadPendingInvites(this.householdId!);
        this.toastService.success('Invitation resent', { durationMs: 3000 });
      },
      error: (e) => {
        this.log.error('Failed to resend invitation', e, { invitationId });
        this.toastService.error('Failed to resend invitation', { durationMs: 6000 });
      }
    });
  }

  revokeInvitation(invitationId: number): void {
    this.invitationService.revokeInvitation(invitationId).subscribe({
      next: () => {
        this.loadPendingInvites(this.householdId!);
        this.toastService.success('Invitation revoked', { durationMs: 3000 });
      },
      error: (e) => {
        this.log.error('Failed to revoke invitation', e, { invitationId });
        this.toastService.error('Failed to revoke invitation', { durationMs: 6000 });
      }
    });
  }

  async confirmRemoveMember(member: Member): Promise<void> {
    if (!this.householdId || !member?.id) return;

    const label = member.nickname || member.email;
    const ok = await this.confirm.ask({
      title: 'Remove member',
      message: `Remove "${label}" from this household? They will lose access to it.`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmBtnClass: 'btn-danger'
    });

    if (!ok) return;

    this.householdService.removeMember(this.householdId, member.id).subscribe({
      next: () => {
        this.toastService.success('Member removed', { durationMs: 3000 });
        this.loadHousehold(this.householdId!);
      },
      error: (e) => {
        this.log.error('Failed to remove member', e, { householdId: this.householdId, userId: member.id });
        const msg = e?.status === 403
          ? 'Only the household owner can remove members'
          : 'Failed to remove member';
        this.toastService.error(msg, { durationMs: 6000 });
      }
    });
  }

  // ==================== STORES SECTION ====================
  storeRowActions(s: Store): RowAction[] {
    return [
      { label: 'Edit', icon: this.faEdit, variant: 'secondary', tone: 'solid', onClick: () => this.openEditStoreModal(s) },
      { label: 'Set default', icon: this.faPin, variant: 'primary', disabled: s.is_default, tone: 'solid', onClick: () => this.setDefaultStore(s) },
      { label: 'Delete', icon: this.faRemove, variant: 'danger', tone: 'outline', onClick: () => this.confirmDeleteStore(s) },
    ];
  }

  openCreateStoreModal(): void {
    this.storeMode = 'create';
    this.storeMessage = undefined;
    this.activeStore = undefined;
    this.activeStoreId = undefined;

    const isFirst = (this.household?.stores?.length ?? 0) === 0;

    this.storeForm.reset({
      isDefault: isFirst,
      name: '',
      temperatureZone: ''
    });

    this.storeSaving = false;
    this.storeUiModal?.open();
  }

  openEditStoreModal(store: any): void {
    this.storeMode = 'edit';
    this.storeMessage = undefined;
    this.activeStore = store;
    this.activeStoreId = store.id;

    this.storeForm.reset({
      isDefault: !!store.is_default,
      name: store.name ?? '',
      temperatureZone: store.temperature_zone ?? ''
    });

    this.storeSaving = false;
    this.storeUiModal?.open();
  }

  closeStoreModal(): void {
    this.storeUiModal?.close();
  }

  saveStoreFromModal(): void {
    if (this.storeSaving || this.storeForm.invalid || !this.householdId) return;

    this.storeSaving = true;

    const payload: Store = {
      name: this.storeForm.value.name,
      is_default: this.storeForm.value.isDefault,
      temperature_zone: this.storeForm.value.temperatureZone || null
    };

    if (this.storeMode === 'create') {
      this.storeService.add(this.householdId, payload).subscribe({
        next: () => {
          this.toastService.success('Store created', { durationMs: 3000 });
          this.loadHousehold(this.householdId!);
          this.storeSaving = false;
          this.storeUiModal?.close();
        },
        error: (e) => {
          this.log.error('Failed to create store', e, { householdId: this.householdId, payload });
          this.toastService.error('Failed to create store', { durationMs: 5000 });
          this.storeSaving = false;
        }
      });
    } else {
      const id = this.activeStoreId!;
      this.storeService.update(id, payload).subscribe({
        next: () => {
          this.toastService.success('Store saved', { durationMs: 3000 });
          this.loadHousehold(this.householdId!);
          this.storeSaving = false;
          this.storeUiModal?.close();
        },
        error: (e) => {
          this.log.error('Failed to update store', e, { storeId: id, payload });
          this.toastService.error('Failed to save store', { durationMs: 5000 });
          this.storeSaving = false;
        }
      });
    }
  }

  setDefaultStore(store: any): void {
    if (!store?.id) return;
    if (store.is_default) return;

    const payload: Store = {
      name: store.name,
      is_default: true
    };

    this.storeService.update(store.id, payload).subscribe({
      next: () => {
        this.toastService.success(`Set "${store.name}" as default`, { durationMs: 3000 });
        this.loadHousehold(this.householdId!);
      },
      error: (e) => {
        this.log.error('Failed to set default store', e, { storeId: store?.id });
        this.toastService.error('Failed to set default store', { durationMs: 5000 });
      }
    });
  }

  async confirmDeleteStore(store: any): Promise<void> {
    if (!store?.id) return;

    const ok = await this.confirm.ask({
      title: 'Delete store',
      message: `Delete store "${store.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmBtnClass: 'btn-danger'
    });

    if (!ok) return;

    this.storeService.delete(store.id).subscribe({
      next: () => {
        this.toastService.success('Store deleted', { durationMs: 3000 });
        this.loadHousehold(this.householdId!);
      },
      error: (e) => {
        this.log.error('Failed to delete store', e, { storeId: store?.id });
        this.toastService.error('Failed to delete store', { durationMs: 5000 });
      }
    });
  }

  // ==================== TASKS SECTION ====================

  onTaskToggle(taskKey: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedTasks.includes(taskKey)) {
        this.selectedTasks = [...this.selectedTasks, taskKey];
      }
    } else {
      this.selectedTasks = this.selectedTasks.filter(t => t !== taskKey);
    }
  }

  executeSelectedTasks(): void {
    if (!this.selectedTasks || this.selectedTasks.length === 0) {
      this.toastService.error('Please select at least one task to execute.', { durationMs: 3000 });
      return;
    }

    if (!this.householdId) return;

    this.tasksExecuting = true;
    this.householdService.executeTasks(this.householdId, this.selectedTasks).subscribe({
      next: (data) => {
        this.log.info('Executed household tasks', { householdId: this.householdId, response: data });
        this.toastService.success('Executed Household Tasks', { durationMs: 3000 });
        this.loadHousehold(this.householdId!);
        this.tasksExecuting = false;
        this.selectedTasks = []; // Reset selection after execution
      },
      error: (e) => {
        this.log.error('Failed executing household tasks', e, { householdId: this.householdId, selectedTasks: this.selectedTasks });
        this.tasksExecuting = false;
      }
    });
  }

  // ==================== EXPORT SECTION ====================
  openExportPromptModal(): void {
    this.stockPrompt = null;
    this.stockPromptCopied = false;
    this.stockPromptLoading = true;
    this.exportPromptModal?.open();

    this.householdService.getStockPrompt(this.householdId!).subscribe({
      next: (data) => {
        this.stockPrompt = data.prompt;
        this.stockPromptLoading = false;
      },
      error: (e) => {
        this.log.error('Failed to load stock prompt', e, { householdId: this.householdId });
        this.toastService.error('Failed to load stock prompt', { durationMs: 5000 });
        this.stockPromptLoading = false;
        this.exportPromptModal?.close();
      }
    });
  }

  copyStockPrompt(): void {
    if (!this.stockPrompt) return;

    navigator.clipboard.writeText(this.stockPrompt).then(() => {
      this.stockPromptCopied = true;
      clearTimeout(this.copyResetTimer);
      this.copyResetTimer = setTimeout(() => {
        this.stockPromptCopied = false;
      }, 2000);
    });
  }

  // ==================== PROVIDERS SECTION ====================
  getProviderLogoPath(providerKey: string): string {
    return 'assets/img/provider-logos/' + providerKey.toLowerCase().replace(/ /g, '-') + '.png';
  }

  loadProviders(householdId: number): void {
    this.loadingProviders = true;
    this.householdService.getProviderSettings(householdId).subscribe({
      next: (data) => {
        this.providers = data.slice().sort((a, b) => a.priority - b.priority);
        this.loadingProviders = false;
      },
      error: (e) => {
        this.log.error('Failed to load provider settings', e, { householdId });
        this.loadingProviders = false;
      },
    });
  }

  async toggleProvider(provider: HouseholdProviderSetting): Promise<void> {
    const newValue = !provider.is_enabled;

    if (!newValue) {
      const ok = await this.confirm.ask({
        title: 'Disable provider',
        message: `Disabling "${provider.provider_name || provider.provider_key}" will delete all related product data synced from this provider. This cannot be undone.`,
        confirmText: 'Disable',
        cancelText: 'Cancel',
        confirmBtnClass: 'btn-danger',
      });
      if (!ok) return;
    }

    this.householdService.patchProviderSetting(this.householdId!, provider.provider_key, { is_enabled: newValue }).subscribe({
      next: () => {
        provider.is_enabled = newValue;
      },
      error: (e) => {
        this.log.error('Failed to update provider setting', e, { providerKey: provider.provider_key });
        this.toastService.error('Failed to update provider setting', { durationMs: 4000 });
      },
    });
  }

  dropProvider(event: CdkDragDrop<HouseholdProviderSetting[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.providers, event.previousIndex, event.currentIndex);
    const reordered = this.providers.map((p, i) => ({ ...p, priority: i }));
    this.providers = reordered;
    this.householdService.replaceProviderSettings(this.householdId!, reordered).subscribe({
      error: (e) => {
        this.log.error('Failed to reorder providers', e, { householdId: this.householdId });
        this.toastService.error('Failed to save provider order', { durationMs: 4000 });
      },
    });
  }

  // ==================== DANGER SECTION ====================
  async openDeleteHouseholdModal(): Promise<void> {
    if (this.household?.is_default) {
      this.toastService.error('You cannot delete your default household. Switch the default household first.', { durationMs: 4000 });
      return;
    }

    if (!this.household?.name) {
      this.toastService.error('Household not loaded yet.', { durationMs: 3000 });
      return;
    }

    const ok = await this.confirm.ask({
      title: 'Delete household',
      message: `Delete household "${this.household.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmBtnClass: 'btn-danger'
    });

    if (!ok) return;
    this.deleteHousehold();
  }

  private deleteHousehold(): void {
    if (this.deletingHousehold || !this.householdId) return;

    this.deletingHousehold = true;

    this.householdService.delete(this.householdId).subscribe({
      next: () => {
        this.toastService.success('Household deleted', { durationMs: 3000 });
        this.appEvents.notifyHouseholdChanged();
        this.deletingHousehold = false;
        this.router.navigate(['/profile']);
      },
      error: (e) => {
        this.log.error('Failed to delete household', e, { householdId: this.householdId });
        this.toastService.error('Failed to delete household', { durationMs: 6000 });
        this.deletingHousehold = false;
      }
    });
  }

  async openClearProductsModal(): Promise<void> {
    const ok = await this.confirm.ask({
      title: 'Clear products',
      message: 'This will clear everything regarding products for this household, including stock levels, desired stock settings, and shopping list item references. This cannot be undone.',
      confirmText: 'Clear products',
      cancelText: 'Cancel',
      confirmBtnClass: 'btn-danger'
    });

    if (!ok) return;
    this.clearProducts();
  }

  private clearProducts(): void {
    if (this.clearingProducts || !this.householdId) return;

    this.clearingProducts = true;

    this.householdService.clearProducts(this.householdId).subscribe({
      next: () => {
        this.toastService.success('Products cleared', { durationMs: 3000 });
        this.clearingProducts = false;
      },
      error: (e) => {
        this.log.error('Failed to clear products', e, { householdId: this.householdId });
        this.toastService.error('Failed to clear products', { durationMs: 6000 });
        this.clearingProducts = false;
      }
    });
  }
}
